import { useMemo, memo, useEffect, useRef } from "react";
import StoryCard from "./StoryCard";

// Add component render counter
let storyGridRenderCount = 0;

interface Story {
  _id: string;
  title: string;
  short_description: string;
  medium_description: string;
  type: string;
  context: string;
  city: string;
  region: string;
  is_sponsored: boolean;
  downloads: number;
  sponsor_id?: {
    name: string;
  };
  thumbnail_image?: string;
  __stableKey?: string; // Added stable key property
}

interface StoryGridProps {
  stories: Story[];
  loading?: boolean;
}

const StoryGrid = ({ stories, loading }: StoryGridProps) => {
  // Debug render count
  storyGridRenderCount++;
  console.log(`StoryGrid rendered ${storyGridRenderCount} times`);
  
  // Track when stories array changes its items
  const storiesIdsRef = useRef<string[]>([]);
  useEffect(() => {
    const currentIds = stories.map(s => s._id);
    const prevIds = storiesIdsRef.current;
    
    if (JSON.stringify(currentIds) !== JSON.stringify(prevIds)) {
      console.log('Stories array changed - new stories:', 
        currentIds.filter(id => !prevIds.includes(id)).length);
      storiesIdsRef.current = currentIds;
    }
  }, [stories]);

  // Create skeleton loading placeholder
  const renderLoadingPlaceholder = useMemo(() => {
    if (!loading) return null;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(8)].map((_, i) => (
          <div
            key={`skeleton-${i}`}
            className="bg-card animate-pulse rounded-sm border border-border/50 border-l-4 border-l-crimson shadow-sm overflow-hidden"
          >
            <div className="aspect-[4/3] bg-muted" />
            <div className="p-4 space-y-3">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
              <div className="h-4 bg-muted rounded w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }, [loading]);

  // Create empty state message
  const renderEmptyState = useMemo(() => {
    if (loading || stories.length > 0) return null;
    
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold mb-2">No Stories Found</h3>
        <p className="text-muted-foreground">
          Try adjusting your search or filters to find what you're looking for.
        </p>
      </div>
    );
  }, [loading, stories.length]);

  // CRITICAL FIX: Create references to story objects to ensure identity stability
  const stableStoriesRef = useRef<Record<string, Story>>({});
  
  // Update the stable stories reference map whenever stories change
  useEffect(() => {
    stories.forEach(story => {
      stableStoriesRef.current[story._id] = story;
    });
  }, [stories]);

  // Process stories with stable references
  const processedStories = useMemo(() => {
    return stories.map(story => {
      // Use the stable reference from the ref if it exists
      const stableStory = stableStoriesRef.current[story._id];
      if (stableStory && stableStory.__stableKey) {
        return stableStory;
      }
      // Otherwise use the original
      return story;
    });
  }, [stories]);

  // Render story grid only when needed
  const renderStoryGrid = useMemo(() => {
    if (loading || stories.length === 0) return null;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {processedStories.map((story) => (
          <StoryCard
            key={story.__stableKey || story._id}
            story={story}
          />
        ))}
      </div>
    );
  }, [processedStories, loading]);

  return (
    <>
      {/* Debug info */}
      {/* <div className="mb-4 bg-muted-foreground/10 p-2 rounded text-xs">
        Grid Render Count: {storyGridRenderCount} | Stories: {stories.length}
      </div> */}
      
      {renderLoadingPlaceholder}
      {renderEmptyState}
      {renderStoryGrid}
    </>
  );
};

// CRITICAL FIX: Use custom comparison function for memoization
export default memo(StoryGrid, (prevProps, nextProps) => {
  // If loading state changes, we need to re-render
  if (prevProps.loading !== nextProps.loading) {
    return false;
  }
  
  // If the stories array length changes, we need to re-render
  if (prevProps.stories.length !== nextProps.stories.length) {
    return false;
  }
  
  // Check if the stories are the same by comparing their IDs and stable keys
  const prevIds = prevProps.stories.map(s => s.__stableKey || s._id).join(',');
  const nextIds = nextProps.stories.map(s => s.__stableKey || s._id).join(',');
  
  return prevIds === nextIds;
});